<template>

  <table :id="tableId" class="table stihl-table" :class="$props.class">
    <caption v-text="caption" v-if="caption" />

    <thead v-if="showHeaders">
      <tr>
        <th
          scope="col"
          v-for="(col, name) in columns"
          @click="sortBy(col, name)"
          :key="name"
          :class="{ sortable: col.sortable, [headerClass]: headerClass, [col.columnClass as string]: col.columnClass }">

          {{ col.label }}&nbsp;
          <span v-if="col.sortable" :class="{ 'only-on-hover': name !== sort.colName }">
            <img
              src="@/assets/img/icons/chevron-up.svg"
              v-if="(name !== sort.colName || sort.dir === 'up')"
              alt="up" />
            <img
              src="@/assets/img/icons/chevron-down.svg"
              v-if="(name !== sort.colName || sort.dir === 'down')"
              alt="down" />
          </span>

        </th>
      </tr>
    </thead>

    <tbody v-if="!total"><tr><td :class="'mute ' + tdClass" :colspan="columns? Object.entries(columns).length : 1">
      {{ $t('adm.multiselect.noItems') }}
    </td></tr></tbody>

    <tbody v-else>
      <tr
        :key="i"
        v-for="(item, i) in entities"
        v-show="pageSize <= 0 || (i >= first && i <= last)"
        :class="{ 'is-disabled': !!isItemActive && !isItemActive(item) }">
        <td v-for="(col, colID) in columns" @click="emitItemClick(item, i)" :key="colID" :class="tdClass">
          <slot :name="colID" v-if="col.selfRender" v-bind:item="item" v-bind:index="i" />
          <span v-else v-html="decorate(item, colID, col)" class="d-inline-block" :class="col.itemClass" />
          <copy-pasta :data="decorate(item, colID, col)" rootClass="ms-1" iconClass="small-icon" v-if="col.copyable" />
        </td>
      </tr>
      <slot name="extra" />
    </tbody>
  </table>

  <nav
    class="d-flex justify-content-between align-items-center stihl-table-pg"
    aria-label="Table pagination"
    v-if="initPageSize > 0">

    <div class="stihl-table-pg-size">
      <label for="pageSize" class="stihl-table-pg-ipp">{{ $t('adm.label.items-per-page') }}</label>
      <select
        id="pageSize"
        name="pageSize"
        class="form-select"
        v-model="pageSize"
        @input="onPageSizeChange(pageSize)">
        <option v-for="size in pageSizes" :key="size">{{ size }}</option>
      </select>
      <span class="stihl-table-pg-ift">{{ $t('adm.label.items-from-total', [from, to, total, itemLabel]) }}
      </span>
    </div>

    <ul class="stihl-table-pg-links">
      <li>
        <select
          name="pageNr"
          class="form-select"
          aria-label="select row"
          v-model="page"
          @input="$emit('pageChange', $event)">
          <option v-for="page in pages" :key="page">{{ page }}</option>
        </select>
        <div class="stihl-table-pg-total">{{ $t('adm.label.page-from-pages', [pages]) }}</div>
      </li>
      <!-- eslint-disable vuejs-accessibility/click-events-have-key-events -->
      <li><a class="stihl-pg-first" :class="{ disabled: prevDisabled }" aria-label="first" @click="selectPage(1)">
        <stihl-icon icon="chevron-line-left" /></a></li>
      <li><a class="stihl-pg-prev" :class="{ disabled: prevDisabled }" aria-label="previous" @click="prev">
        <stihl-icon icon="chevron-left" /></a></li>
      <li><a aria-label="stihl-pg-next" :class="{ disabled: nextDisabled }" @click="next">
        <stihl-icon icon="chevron-right" /></a></li>
      <li><a class="stihl-pg-last" :class="{ disabled: nextDisabled }" aria-label="last" @click="selectPage(pages)">
        <stihl-icon icon="chevron-line-right" /></a></li>
      <!-- eslint-enable vuejs-accessibility/click-events-have-key-events -->
    </ul>
  </nav>

</template>

<script lang="ts">
import type { PropType } from 'vue';
import { defineComponent } from 'vue';
import { Logger } from 'oidc-client-ts';
import { arraySize } from '@/components/utils';
import StihlIcon from '@/components/ui/StihlIcon.vue';
import CopyPasta from '@/components/ui/CopyPasta.vue';

export interface TableColumn<ItemType> {
  label: string // label to display in header
  sortable?: boolean
  selfRender?: boolean // this column will render itself
  columnClass?: string // set on specific column
  itemClass?: string // set on items of this column
  decorator?: (item: ItemType) => string
  copyable?: boolean // copy value to clipboard
}

export type TableColumns<ItemType = TableRow> = Record<string, TableColumn<ItemType>>;

export type TableRow = Record<string, any>;

type SortDir = null | 'up' | 'down';

export default defineComponent({
  name: 'DataTable',
  components: { CopyPasta, StihlIcon },
  emits: ['pageSizeChange', 'pageChange', 'itemClick'],
  props: {
    tableId: { type: String, default: null },
    caption: { type: String, default: null },
    showHeaders: { type: Boolean, default: true },
    columns: { type: Object as PropType<TableColumns>, required: true },
    items: { type: Array as PropType<TableRow[]>, required: true },
    initPageSize: { type: Number, default: 0 }, // 0 = no pagination
    pageSizes: { type: Array as PropType<number[]>, default: () => [10, 15, 25, 35, 100] },
    class: { type: String, default: 'table-striped table-hover' },
    headerClass: { type: String, default: 'table-dark' },
    tdClass: { type: String, default: '' },
    itemLabel: { type: String, default: '<items>' },
    // mark deactivated items/rows in table
    isItemActive: { type: Function as PropType<(i: TableRow) => boolean>, default: null },
  },
  data() {
    return {
      page: 1,
      pageSize: 15,
      sort: {
        colName: null as string | null,
        dir: null as SortDir,
        hover: null,
      },
    };
  },
  methods: {

    emitItemClick(item: TableRow, i: number) {
      // if item not disabled
      Logger.debug('dataTable', 'itemClick', i);
      if (!this.isItemActive || this.isItemActive(item)) this.$emit('itemClick', i);
    },

    sortBy(config: TableColumn<TableRow>, name: string) {
      if (!config.sortable) return; // not sortable column

      Logger.debug('dataTable', 'sort-by', name, config);
      const nextDir = this.nextSortDir(name);

      if (nextDir) {
        const compare = (a: TableRow, b: TableRow) => {
          const x = config.decorator ? config.decorator(a) : a[name];
          const y = config.decorator ? config.decorator(b) : b[name];
          if (x < y) return nextDir === 'up' ? -1 : 1;
          if (x > y) return nextDir === 'up' ? 1 : -1;
          return 0;
        };
        this.entities.sort(compare);
      }
      this.sort.colName = name;
      this.sort.dir = nextDir;
    },

    nextSortDir(colName: string): SortDir {
      if (this.sort.colName === colName && this.sort.dir === 'up') return 'down';
      return 'up';
    },

    /**
     * can transform the way a value is displayed
     * @param item table item/row
     * @param colID columnKey
     * @param col column configuration
     * @returns {*} display value
     */
    decorate(item: TableRow, colID: string, col: TableColumn<TableRow>) {
      return col.decorator ? col.decorator(item) : item[colID];
    },
    next() {
      if (this.page < this.pages) this.selectPage(this.page + 1);
    },
    prev() {
      if (this.page > 1) this.selectPage(this.page - 1);
    },
    selectPage(page: number) {
      this.page = page;
    },
    onPageSizeChange(pageSize: number) {
      this.selectPage(1); // reset current page
      this.$emit('pageSizeChange', pageSize);
    },
  },

  computed: {
    entities() {
      return this.items;
    },
    first(): number {
      return this.pageSize > 0 ? (this.page - 1) * this.pageSize : 0;
    },
    last(): number {
      return this.pageSize > 0 ? this.page * this.pageSize - 1 : this.entities.length - 1;
    },
    pages(): number {
      const items = arraySize(this.entities);
      const size = this.pageSize ?? 1;
      const x = Math.floor(items / size) + (items % size > 0 ? 1 : 0);
      Logger.debug('dataTable', `pages: ${x} of size ${size} for ${items} items`);
      return x;
    },
    prevDisabled(): boolean {
      return this.pages === 0 || this.page === 1;
    },
    nextDisabled(): boolean {
      return this.pages === 0 || this.page === this.pages;
    },
    from(): number {
      return Math.min(this.entities.length, (this.page - 1) * this.pageSize + 1);
    },
    to(): number {
      return Math.min(this.entities.length, (this.page) * this.pageSize);
    },
    total(): number {
      return this.entities.length;
    },
  },

  mounted() {
    // Logger.info('users', 'items:', JSON.stringify(this.items));
    if (this.initPageSize > -1) this.pageSize = this.initPageSize;
  },
});
</script>
